import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ChevronDown, CheckCircle, Star, Phone, Mail, MapPin, MessageCircle, Send, ArrowRight } from 'lucide-react';
import {
  ODS_LIST, SERVICES, TESTIMONIALS, GALLERY_IMAGES, TEAM_MEMBERS,
  scrollToSection, type Lang
} from '@/lib/index';

// ─── Logo SVG del document ───────────────────────────────────────────────────
const LiberumLogoSVG = ({ size = 80 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Logo de Liberum">
    <defs>
      <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0A1F44"/>
        <stop offset="100%" stopColor="#2A9D8F"/>
      </linearGradient>
    </defs>
    <circle cx="60" cy="60" r="58" fill="url(#logoGrad)" stroke="white" strokeWidth="2" strokeOpacity="0.3"/>
    <text x="60" y="52" fontFamily="Montserrat,Arial Black,sans-serif" fontSize="18" fontWeight="900" fill="white" textAnchor="middle" letterSpacing="4">LIBER</text>
    <text x="60" y="76" fontFamily="Montserrat,Arial Black,sans-serif" fontSize="18" fontWeight="900" fill="white" textAnchor="middle" letterSpacing="4">UM</text>
    <line x1="20" y1="84" x2="100" y2="84" stroke="white" strokeWidth="1" strokeOpacity="0.5"/>
    <text x="60" y="100" fontFamily="Arial,sans-serif" fontSize="7" fill="white" textAnchor="middle" letterSpacing="1" fillOpacity="0.85">VACANCES SENSE BARRERES</text>
  </svg>
);

// ─── Animation Variants ─────────────────────────────────────────────────────
const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// ─── InView wrapper ─────────────────────────────────────────────────────────
function InViewSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px 0px' });
  return (
    <motion.div ref={ref} variants={staggerContainer} initial="hidden" animate={inView ? 'visible' : 'hidden'} className={className}>
      {children}
    </motion.div>
  );
}

// ─── Props ───────────────────────────────────────────────────────────────────
interface HomeProps { lang: Lang; }

export default function Home({ lang }: HomeProps) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', lang: lang });
  const [formSent, setFormSent] = useState(false);
  const [activeGallery, setActiveGallery] = useState<number | null>(null);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setParallaxY(window.scrollY * 0.4);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const t = (ca: string, es: string) => lang === 'ca' ? ca : es;

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormSent(true);
  };

  return (
    <>
      {/* ══════ HERO ══════ */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-label={t('Secció principal', 'Sección principal')}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/mallorca_3.jpeg)', transform: `translateY(${parallaxY}px)`, willChange: 'transform' }}
          role="img" aria-label={t('Costa de Mallorca amb aigüa turquesa', 'Costa de Mallorca con agua turquesa')}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,31,68,0.72) 0%, rgba(10,31,68,0.5) 50%, rgba(10,31,68,0.80) 100%)' }} aria-hidden="true" />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center text-white pt-16">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>
            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tight mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              LIBERUM
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl font-light text-white/90 mb-2 tracking-wide">
              {t('Vacances sense barreres', 'Vacaciones sin barreras')}
            </p>
            <div className="flex items-center justify-center gap-4 my-6" aria-hidden="true">
              <div className="h-px w-16 bg-white/40" />
              <div className="w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #2A9D8F, #38B2AC)' }} />
              <div className="h-px w-16 bg-white/40" />
            </div>
            <p className="text-base sm:text-lg lg:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed mb-10">
              {t('El turisme accessible que et mereixes. Allotjaments 100% verificats, transport adaptat i experiències inclusives al cor de Mallorca.',
                'El turismo accesible que te mereces. Alojamientos 100% verificados, transporte adaptado y experiencias inclusivas en el corazón de Mallorca.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                onClick={() => scrollToSection('services')}
                className="group flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-base text-white transition-all duration-200 hover:opacity-90 hover:shadow-2xl hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #2A9D8F 100%)' }}
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              >
                {t('Descobreix les teves vacances accessibles', 'Descubre tus vacaciones accesibles')}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </motion.button>
              <motion.button
                onClick={() => scrollToSection('contact')}
                className="flex items-center gap-2 px-8 py-4 rounded-2xl font-semibold text-base text-white border-2 border-white/40 hover:border-white hover:bg-white/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              >
                {t('Contacta\'ns', 'Contáctanos')}
              </motion.button>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-16 flex flex-wrap justify-center gap-8 sm:gap-12"
              role="list" aria-label={t('Estadístiques de Liberum', 'Estadísticas de Liberum')}
            >
              {[
                { value: '100%', label: t('Allotjaments verificats', 'Alojamientos verificados') },
                { value: '33€', label: t('Des de / persona', 'Desde / persona') },
                { value: '24h', label: t('Atenció al client', 'Atención al cliente') },
              ].map((stat) => (
                <div key={stat.label} role="listitem" className="text-center">
                  <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>{stat.value}</div>
                  <div className="text-xs text-white/70 mt-1 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 flex flex-col items-center gap-1 cursor-pointer"
          animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}
          onClick={() => scrollToSection('about')}
          aria-label={t('Desplaça\'t avall', 'Desplázate abajo')} role="button" tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && scrollToSection('about')}
        >
          <span className="text-xs uppercase tracking-widest">{t('Descobreix', 'Descubre')}</span>
          <ChevronDown size={20} aria-hidden="true" />
        </motion.div>
      </section>

      {/* ══════ SOBRE LIBERUM ══════ */}
      <section id="about" className="py-20 lg:py-28 bg-white" aria-labelledby="about-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <InViewSection className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div variants={scaleIn} className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/5]">
                <img src="/images/about_history_new.webp" alt={t('Parella gran gaudint amb alegria d\'unes vacances accessibles', 'Pareja mayor disfrutando con alegría de unas vacaciones accesibles')} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 opacity-20" style={{ background: 'linear-gradient(to top, #0A1F44, transparent)' }} aria-hidden="true" />
              </div>
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6 }} className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4 border border-border" aria-hidden="true">
                <div className="text-2xl font-bold text-primary" style={{ fontFamily: 'Montserrat, sans-serif' }}>2024</div>
                <div className="text-xs text-muted-foreground">{t('Any de fundació', 'Año de fundación')}</div>
              </motion.div>
            </motion.div>

            <div>
              <motion.div variants={fadeInUp}>
                {/* Títol gran: La nostra història (sense subtítol extra) */}
                <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-4 border border-accent/30 rounded-full px-3 py-1">
                  {t('La nostra història', 'Nuestra historia')}
                </span>
                <h2 id="about-title" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-primary mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('La nostra història', 'Nuestra historia')}
                </h2>
              </motion.div>
              <motion.div variants={fadeInUp} className="space-y-5 text-base lg:text-lg text-muted-foreground leading-relaxed">
                <p>
                  {t('Tot va començar un estiu, quan cinc amigues van decidir fer un viatge juntes. La seva amiga Aina, que en aquell moment feia servir cadira de rodes, va tenir molts problemes per trobar un allotjament realment accessible, tot i que molts s\'anunciaven com a "adaptats".',
                    'Todo comenzó un verano, cuando cinco amigas decidieron hacer un viaje juntas. Su amiga Aina, que en ese momento usaba silla de ruedas, tuvo muchos problemas para encontrar un alojamiento realmente accesible, aunque muchos se anunciaban como "adaptados".')}
                </p>
                <p>
                  {t('Aquella experiència els va obrir els ulls: en ple segle XXI, planificar unes vacances inclusives seguia sent una missió impossible. Durant una conversa a la platja, va néixer Liberum.',
                    'Esa experiencia les abrió los ojos: en pleno siglo XXI, planificar unas vacaciones inclusivas seguía siendo una misión imposible. Durante una conversación en la playa, nació Liberum.')}
                </p>
                <p className="font-medium text-foreground">
                  {t('La nostra missió és clara: que tothom pugui gaudir del plaer de viatjar sense barreres. Perquè creiem que les vacances dignes no haurien de ser un privilegi, sinó un dret de tothom.',
                    'Nuestra misión es clara: que todos puedan disfrutar del placer de viajar sin barreras. Porque creemos que las vacaciones dignas no deberían ser un privilegio, sino un derecho de todos.')}
                </p>
              </motion.div>
              <motion.div variants={staggerContainer} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: '✅', text: t('Verificació personal de cada allotjament', 'Verificación personal de cada alojamiento') },
                  { icon: '💛', text: t('Tracte humà i proper', 'Trato humano y cercano') },
                  { icon: '🔒', text: t('Garantia d\'accessibilitat real', 'Garantía de accesibilidad real') },
                  { icon: '🌍', text: t('Compromís social i inclusió', 'Compromiso social e inclusión') },
                ].map((v) => (
                  <motion.div key={v.text} variants={fadeInUp} className="flex items-start gap-3 p-3 rounded-xl hover:bg-muted transition-colors">
                    <span className="text-lg mt-0.5" aria-hidden="true">{v.icon}</span>
                    <span className="text-sm font-medium text-foreground">{v.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </InViewSection>
        </div>
      </section>

      {/* ══════ COMPROMÍS ODS ══════ */}
      <section id="ods" className="py-20 lg:py-28" style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #1a3a6b 50%, #0d3b5c 100%)' }} aria-labelledby="ods-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <InViewSection>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-3 border border-accent/40 rounded-full px-3 py-1">
                {t('Responsabilitat social', 'Responsabilidad social')}
              </span>
              <h2 id="ods-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('Compromesos amb l\'Agenda 2030', 'Comprometidos con la Agenda 2030')}
              </h2>
              <p className="text-white/70 text-base lg:text-lg max-w-2xl mx-auto">
                {t('El nostre projecte contribueix activament a tres Objectius de Desenvolupament Sostenible de les Nacions Unides.',
                  'Nuestro proyecto contribuye activamente a tres Objetivos de Desarrollo Sostenible de las Naciones Unidas.')}
              </p>
            </motion.div>

            <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {ODS_LIST.map((ods) => (
                <motion.article
                  key={ods.number}
                  variants={scaleIn}
                  className="bg-white/8 backdrop-blur rounded-2xl p-6 lg:p-8 border border-white/10 hover:bg-white/12 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  {/* ODS Logo oficial en lloc del numero */}
                  <div className="flex items-start gap-4 mb-4">
                    <img
                      src={ods.logo}
                      alt={t(`Logo ODS ${ods.number}: ${ods.title}`, `Logo ODS ${ods.number}: ${ods.titleEs}`)}
                      className="w-16 h-16 rounded-xl shrink-0 object-cover"
                    />
                    <div>
                      <div className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1">{t('ODS', 'ODS')} {ods.number}</div>
                      <h3 className="font-bold text-white text-base leading-tight">
                        {lang === 'ca' ? ods.title : ods.titleEs}
                      </h3>
                    </div>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {lang === 'ca' ? ods.description : ods.descriptionEs}
                  </p>
                </motion.article>
              ))}
            </motion.div>
          </InViewSection>
        </div>
      </section>

      {/* ══════ SERVEIS ══════ */}
      <section id="services" className="py-20 lg:py-28 bg-white" aria-labelledby="services-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <InViewSection>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-3 border border-accent/30 rounded-full px-3 py-1">
                {t('El que oferim', 'Lo que ofrecemos')}
              </span>
              <h2 id="services-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('Serveis pensats per a tu', 'Servicios pensados para ti')}
              </h2>
              <p className="text-muted-foreground text-base lg:text-lg max-w-2xl mx-auto">
                {t('Cada detall del teu viatge, gestionat per nosaltres amb la màxima professionalitat i cura.',
                  'Cada detalle de tu viaje, gestionado por nosotros con la máxima profesionalidad y cuidado.')}
              </p>
            </motion.div>

            {/* Servei estrella */}
            <motion.div variants={scaleIn} className="mb-10">
              <article className="relative rounded-3xl overflow-hidden" aria-label={t('Servei estrella: Liberum Sense Barreres', 'Servicio estrella: Liberum Sin Barreras')}>
                <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #2A9D8F 100%)' }} aria-hidden="true" />
                <div className="relative z-10 p-8 lg:p-12">
                  <div className="flex flex-wrap items-start justify-between gap-6">
                    <div className="flex-1 min-w-[260px]">
                      <span className="bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3 inline-block">
                        ⭐ {t('Servei estrella', 'Servicio estrella')}
                      </span>
                      <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Liberum Sense Barreres
                      </h3>
                      <p className="text-white/80 text-base lg:text-lg leading-relaxed max-w-xl mb-6">
                        {t('El paquet integral 100% accessible. Verifiquem personalment cada allotjament per evitar falses promeses. Tu només has de gaudir.',
                          'El paquete integral 100% accesible. Verificamos personalmente cada alojamiento para evitar falsas promesas. Tú solo tienes que disfrutar.')}
                      </p>
                      <ul className="space-y-2 mb-8" aria-label={t('Inclou', 'Incluye')}>
                        {[
                          t('Allotjament 100% accessible verificat', 'Alojamiento 100% accesible verificado'),
                          t('Transport adaptat inclòs', 'Transporte adaptado incluido'),
                          t('Activitats i excursions inclusives', 'Actividades y excursiones inclusivas'),
                          t('Assessorament personalitzat previ', 'Asesoramiento personalizado previo'),
                          t('Assistència 24h durant l\'estada', 'Asistencia 24h durante la estancia'),
                        ].map((item) => (
                          <li key={item} className="flex items-center gap-2 text-white/85 text-sm">
                            <CheckCircle size={16} className="text-accent shrink-0" aria-hidden="true" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      <motion.button
                        onClick={() => scrollToSection('contact')}
                        className="flex items-center gap-2 bg-white text-primary font-semibold px-6 py-3 rounded-xl hover:bg-white/90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      >
                        {t('Sol·licita informació', 'Solicitar información')}
                        <ArrowRight size={16} aria-hidden="true" />
                      </motion.button>
                    </div>
                    <div className="text-center bg-white/10 rounded-2xl p-6 min-w-[160px]">
                      <div className="text-xs text-white/60 uppercase tracking-wider mb-1">{t('Des de', 'Desde')}</div>
                      <div className="text-5xl font-extrabold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>33€</div>
                      <div className="text-white/60 text-sm">{t('per persona', 'por persona')}</div>
                      <div className="text-xs text-white/50 mt-1">{t('IVA inclòs', 'IVA incluido')}</div>
                    </div>
                  </div>
                </div>
              </article>
            </motion.div>

            {/* Altres serveis */}
            <motion.div variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {SERVICES.map((service) => (
                <motion.article key={service.title} variants={fadeInUp} className="bg-card rounded-2xl p-6 border border-border hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
                  <div className="text-3xl mb-4" aria-hidden="true">{service.icon}</div>
                  <h3 className="font-bold text-foreground text-base mb-2 group-hover:text-primary transition-colors">
                    {lang === 'ca' ? service.title : service.titleEs}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {lang === 'ca' ? service.description : service.descriptionEs}
                  </p>
                </motion.article>
              ))}
            </motion.div>
          </InViewSection>
        </div>
      </section>

      {/* ══════ PER QUÈ ELEGIRNOS ══════ */}
      <section id="why" className="py-20 lg:py-28 bg-muted" aria-labelledby="why-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <InViewSection className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <motion.div variants={fadeInUp}>
                <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-3 border border-accent/30 rounded-full px-3 py-1">
                  {t('La nostra diferència', 'Nuestra diferencia')}
                </span>
                <h2 id="why-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('Per què triar Liberum?', '¿Por qué elegir Liberum?')}
                </h2>
                <p className="text-muted-foreground text-base lg:text-lg leading-relaxed mb-8">
                  {t('No som una agència de viatges convencional. Som un equip de persones compromeses amb la inclusió que entenem de primera mà el que significa viatjar amb mobilitat reduïda.',
                    'No somos una agencia de viajes convencional. Somos un equipo de personas comprometidas con la inclusión que entendemos de primera mano lo que significa viajar con movilidad reducida.')}
                </p>
              </motion.div>
              <motion.ul variants={staggerContainer} className="space-y-4">
                {[
                  { icon: '🔍', title: t('Verificació personal', 'Verificación personal'), desc: t('Visitem i comprovem cada allotjament per garantir l\'accessibilitat real.', 'Visitamos y comprobamos cada alojamiento para garantizar la accesibilidad real.') },
                  { icon: '🤝', title: t('Servei integral', 'Servicio integral'), desc: t('Ens encarreguem de tot: allotjament, transport i activitats en un sol paquet.', 'Nos encargamos de todo: alojamiento, transporte y actividades en un solo paquete.') },
                  { icon: '💙', title: t('Tracte humà', 'Trato humano'), desc: t('Entenem les necessitats específiques de cada persona i les atenim amb cura i empatia.', 'Entendemos las necesidades específicas de cada persona y las atendemos con cuidado y empatía.') },
                  { icon: '📍', title: t('Ubicació privilegiada', 'Ubicación privilegiada'), desc: t('Basades a Pollença, coneixem Mallorca a fons i les millors opcions accessibles de l\'illa.', 'Con sede en Pollença, conocemos Mallorca a fondo y las mejores opciones accesibles de la isla.') },
                ].map((item) => (
                  <motion.li key={item.title} variants={fadeInUp} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-border hover:shadow-sm transition-shadow">
                    <span className="text-2xl shrink-0 mt-0.5" aria-hidden="true">{item.icon}</span>
                    <div>
                      <h3 className="font-semibold text-foreground text-sm mb-1">{item.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
            <motion.div variants={scaleIn} className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl aspect-square">
                <img src="/images/mallorca_5.jpeg" alt={t('Poble costaner de Pollença, Mallorca', 'Pueblo costero de Pollença, Mallorca')} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-border max-w-[200px]">
                <div className="flex items-center gap-1 mb-1">{[1,2,3,4,5].map(i => <Star key={i} size={12} className="text-accent fill-accent" aria-hidden="true" />)}</div>
                <p className="text-xs text-foreground font-medium">{t('"L\'única agència que realment verifica l\'accessibilitat"', '"La única agencia que realmente verifica la accesibilidad"')}</p>
                <p className="text-xs text-muted-foreground mt-1">– {t('Clients satisfets', 'Clientes satisfechos')}</p>
              </motion.div>
            </motion.div>
          </InViewSection>
        </div>
      </section>

      {/* ══════ EQUIP ══════ */}
      <section id="team" className="py-20 lg:py-28 bg-white" aria-labelledby="team-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <InViewSection>
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-3 border border-accent/30 rounded-full px-3 py-1">
                {t('Les persones darrere Liberum', 'Las personas detrás de Liberum')}
              </span>
              <h2 id="team-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('El nostre equip', 'Nuestro equipo')}
              </h2>
              <p className="text-muted-foreground text-base lg:text-lg max-w-2xl mx-auto">
                {t('Un equip de cinc amigues amb una missió compartida: fer que viatjar sigui accessible per a tothom.',
                  'Un equipo de cinco amigas con una misión compartida: hacer que viajar sea accesible para todos.')}
              </p>
            </motion.div>

            {/* Foto de grup */}
            <motion.div variants={scaleIn} className="mb-10">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl max-w-2xl mx-auto">
                <img
                  src="/images/team_photo.jpeg"
                  alt={t('L\'equip de Liberum: Maria Magdalena Pastor, Francina Segura, Margalida Ferrer, Alba Garcia i Mariana Moreno, co-fundadores de la cooperativa',
                    'El equipo de Liberum: Maria Magdalena Pastor, Francina Segura, Margalida Ferrer, Alba Garcia y Mariana Moreno, co-fundadoras de la cooperativa')}
                  className="w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 opacity-10" style={{ background: 'linear-gradient(to top, #0A1F44, transparent)' }} aria-hidden="true" />
              </div>
            </motion.div>

            {/* Noms de les integrants */}
            <motion.div variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
              {TEAM_MEMBERS.map((member, idx) => (
                <motion.div key={member.name} variants={fadeInUp} className="text-center p-4 rounded-2xl bg-muted border border-border hover:border-accent hover:shadow-md transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-white text-sm" style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #2A9D8F 100%)' }} aria-hidden="true">
                    {idx + 1}
                  </div>
                  <h3 className="font-bold text-foreground text-xs lg:text-sm mb-1 group-hover:text-primary transition-colors">{member.name}</h3>
                  <p className="text-accent text-xs leading-snug">{lang === 'ca' ? member.role : member.roleEs}</p>
                </motion.div>
              ))}
            </motion.div>
          </InViewSection>
        </div>
      </section>

      {/* ══════ GALERIA & TESTIMONIS ══════ */}
      <section id="gallery" className="py-20 lg:py-28 bg-muted" aria-labelledby="gallery-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <InViewSection>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-3 border border-accent/30 rounded-full px-3 py-1">
                {t('Allotjaments i experiències', 'Alojamientos y experiencias')}
              </span>
              <h2 id="gallery-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('Galeria d\'experiències', 'Galería de experiencias')}
              </h2>
              <p className="text-muted-foreground text-base lg:text-lg max-w-2xl mx-auto">
                {t('Allotjaments adaptats verificats i moments reals de persones que gaudeixen de la llibertat de viatjar sense barreres.',
                  'Alojamientos adaptados verificados y momentos reales de personas que disfrutan de la libertad de viajar sin barreras.')}
              </p>
            </motion.div>

            <motion.div variants={staggerContainer} className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mb-16">
              {GALLERY_IMAGES.map((img, idx) => (
                <motion.div
                  key={img.src}
                  variants={scaleIn}
                  className="relative overflow-hidden rounded-2xl cursor-pointer group aspect-[4/3]"
                  onClick={() => setActiveGallery(activeGallery === idx ? null : idx)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setActiveGallery(activeGallery === idx ? null : idx)}
                  aria-pressed={activeGallery === idx}
                  aria-label={lang === 'ca' ? img.alt : img.altEs}
                >
                  <img src={img.src} alt={lang === 'ca' ? img.alt : img.altEs} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-primary/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <p className="text-white text-xs font-medium">{lang === 'ca' ? img.alt : img.altEs}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Testimonials */}
            <motion.div variants={fadeInUp} className="mb-8">
              <h3 className="text-2xl font-bold text-primary text-center mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('El que diuen els nostres clients', 'Lo que dicen nuestros clientes')}
              </h3>
            </motion.div>
            <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((testimonial) => (
                <motion.article key={testimonial.name} variants={fadeInUp} className="bg-white rounded-2xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow" aria-label={t(`Testimoni de ${testimonial.name}`, `Testimonio de ${testimonial.name}`)}>
                  <div className="flex items-center gap-1 mb-3" aria-label={`${testimonial.rating} estrelles de 5`}>
                    {Array.from({ length: testimonial.rating }).map((_, i) => <Star key={i} size={14} className="text-accent fill-accent" aria-hidden="true" />)}
                  </div>
                  <blockquote className="text-muted-foreground text-sm leading-relaxed mb-4 italic">
                    "{lang === 'ca' ? testimonial.text : testimonial.textEs}"
                  </blockquote>
                  <footer className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-border">
                      <img src={testimonial.image} alt={t(`Foto de ${testimonial.name}`, `Foto de ${testimonial.name}`)} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground text-sm">{testimonial.name}</div>
                      <div className="text-xs text-muted-foreground">{testimonial.location}</div>
                    </div>
                  </footer>
                </motion.article>
              ))}
            </motion.div>
          </InViewSection>
        </div>
      </section>

      {/* ══════ CTA BANNER ══════ */}
      <section className="py-16 lg:py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #2A9D8F 100%)' }} aria-label={t('Crida a l\'acció', 'Llamada a la acción')}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <InViewSection>
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('Preparat per gaudir de Mallorca?', '¿Listo para disfrutar de Mallorca?')}
              </h2>
              <p className="text-white/80 text-base lg:text-lg mb-8 max-w-xl mx-auto">
                {t('Contacta\'ns avui i comença a planificar les vacances accessibles que et mereixes.',
                  'Contáctanos hoy y empieza a planificar las vacaciones accesibles que te mereces.')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button onClick={() => scrollToSection('contact')} className="px-8 py-4 bg-white text-primary font-bold rounded-2xl hover:bg-white/90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  {t('Demana informació gratuïta', 'Pedir información gratuita')}
                </motion.button>
                <a href="https://wa.me/34600000000" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/40 text-white font-bold rounded-2xl hover:border-white hover:bg-white/10 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white">
                  <MessageCircle size={18} aria-hidden="true" />
                  WhatsApp
                </a>
              </div>
            </motion.div>
          </InViewSection>
        </div>
      </section>

      {/* ══════ CONTACTE ══════ */}
      <section id="contact" className="py-20 lg:py-28 bg-white" aria-labelledby="contact-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <InViewSection className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <motion.div variants={fadeInUp}>
                <span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-3 border border-accent/30 rounded-full px-3 py-1">
                  {t('Estam aquí per tu', 'Estamos aquí para ti')}
                </span>
                <h2 id="contact-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('Contacta\'ns', 'Contáctanos')}
                </h2>
                <p className="text-muted-foreground text-base lg:text-lg leading-relaxed mb-8">
                  {t('Estem disponibles per resoldre qualsevol dubte i ajudar-te a planificar les vacances perfectes. No dubtis en posar-te en contacte amb nosaltres.',
                    'Estamos disponibles para resolver cualquier duda y ayudarte a planificar las vacaciones perfectas. No dudes en ponerte en contacto con nosotros.')}
                </p>
              </motion.div>
              <motion.ul variants={staggerContainer} className="space-y-4 mb-8">
                {[
                  { icon: <MapPin size={20} aria-hidden="true" />, title: t('Adreça', 'Dirección'), value: 'Carrer 9, Polígon Industrial Picapedrer\nPollença, Illes Balears 07460', href: 'https://maps.google.com/?q=Pollença+Mallorca' },
                  { icon: <Phone size={20} aria-hidden="true" />, title: t('Telèfon', 'Teléfono'), value: '+34 971 000 000', href: 'tel:+34971000000' },
                  { icon: <Mail size={20} aria-hidden="true" />, title: t('Correu electrònic', 'Correo electrónico'), value: 'info@liberum.coop', href: 'mailto:info@liberum.coop' },
                  { icon: <MessageCircle size={20} aria-hidden="true" />, title: 'WhatsApp', value: t('Escriu-nos per WhatsApp', 'Escríbenos por WhatsApp'), href: 'https://wa.me/34600000000' },
                ].map((item) => (
                  <motion.li key={item.title} variants={fadeInUp}>
                    <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined} className="flex items-start gap-4 p-4 rounded-xl border border-border hover:border-accent hover:bg-muted transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-accent">
                      <span className="text-accent mt-0.5 group-hover:scale-110 transition-transform">{item.icon}</span>
                      <div>
                        <div className="font-semibold text-foreground text-sm">{item.title}</div>
                        <div className="text-muted-foreground text-sm whitespace-pre-line">{item.value}</div>
                      </div>
                    </a>
                  </motion.li>
                ))}
              </motion.ul>
              <motion.div variants={scaleIn} className="rounded-2xl overflow-hidden border border-border shadow-sm h-48 bg-muted relative">
                <iframe
                  title={t('Mapa de Liberum a Pollença, Mallorca', 'Mapa de Liberum en Pollença, Mallorca')}
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24593.62!2d3.0126!3d39.8782!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x129793a0e2f30101%3A0x400191cef4e0fa0!2sPollen%C3%A7a%2C%20Illes%20Balears!5e0!3m2!1sca!2ses!4v1700000000"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  aria-label={t('Mapa de Pollença, Mallorca', 'Mapa de Pollença, Mallorca')}
                />
              </motion.div>
            </div>

            <motion.div variants={scaleIn}>
              {formSent ? (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="h-full flex flex-col items-center justify-center text-center p-8 rounded-3xl bg-muted border border-border">
                  <div className="text-5xl mb-4" aria-hidden="true">✅</div>
                  <h3 className="text-2xl font-bold text-primary mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('Missatge enviat!', '¡Mensaje enviado!')}</h3>
                  <p className="text-muted-foreground text-base">{t('Gràcies pel teu interès en Liberum. Ens posarem en contacte amb tu en les properes 24 hores.', 'Gracias por tu interés en Liberum. Nos pondremos en contacto contigo en las próximas 24 horas.')}</p>
                  <button onClick={() => setFormSent(false)} className="mt-6 text-accent text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-accent rounded">
                    {t('Enviar un altre missatge', 'Enviar otro mensaje')}
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleFormSubmit} className="bg-card rounded-3xl p-6 lg:p-8 border border-border shadow-sm" aria-label={t('Formulari de contacte', 'Formulario de contacto')} noValidate>
                  <h3 className="text-xl font-bold text-foreground mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {t('Demana informació o reserva', 'Pide información o reserva')}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground mb-1.5">
                        {t('Nom complet', 'Nombre completo')} <span className="text-destructive" aria-hidden="true">*</span>
                      </label>
                      <input id="contact-name" type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('El teu nom', 'Tu nombre')} className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all" aria-required="true" />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground mb-1.5">
                        {t('Correu electrònic', 'Correo electrónico')} <span className="text-destructive" aria-hidden="true">*</span>
                      </label>
                      <input id="contact-email" type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t('el.teu@correu.cat', 'tu@correo.es')} className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all" aria-required="true" />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground mb-1.5">
                        {t('Telèfon / WhatsApp', 'Teléfono / WhatsApp')}
                      </label>
                      <input id="contact-phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+34 600 000 000" className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all" />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground mb-1.5">
                        {t('Missatge', 'Mensaje')} <span className="text-destructive" aria-hidden="true">*</span>
                      </label>
                      <textarea id="contact-message" required rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder={t('Explica\'ns les teves necessitats d\'accessibilitat i el viatge que vols fer...', 'Cuéntanos tus necesidades de accesibilidad y el viaje que quieres hacer...')} className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all resize-none" aria-required="true" />
                    </div>
                    <div>
                      <label htmlFor="contact-lang" className="block text-sm font-medium text-foreground mb-1.5">
                        {t('Idioma preferit', 'Idioma preferido')}
                      </label>
                      <select id="contact-lang" value={formData.lang} onChange={(e) => setFormData({ ...formData, lang: e.target.value as Lang })} className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all">
                        <option value="ca">{t('Català', 'Catalán')}</option>
                        <option value="es">{t('Castellà', 'Castellano')}</option>
                      </select>
                    </div>
                    <motion.button type="submit" className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-base text-white transition-all duration-200 hover:opacity-90 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent" style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #2A9D8F 100%)' }} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                      <Send size={16} aria-hidden="true" />
                      {t('Enviar missatge', 'Enviar mensaje')}
                    </motion.button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-4 text-center">
                    {t('Respondrem en menys de 24 hores. Les teves dades no seran compartides amb tercers.', 'Responderemos en menos de 24 horas. Tus datos no serán compartidos con terceros.')}
                  </p>
                </form>
              )}
            </motion.div>
          </InViewSection>
        </div>
      </section>
    </>
  );
}
